import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import { topics } from '@/mocks/site';
import useSeo from '@/hooks/useSeo';

const PAGE_SIZE = 6;

const Topics = () => {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const activeTopic = params.get('topic') ?? 'all';

  const [localQ, setLocalQ] = useState(q);
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setLocalQ(q);
    setVisible(PAGE_SIZE);
  }, [q, activeTopic]);

  const filtered = useMemo(() => {
    const keyword = q.trim().toLowerCase();
    return articles.filter((article) => {
      const matchTopic = activeTopic === 'all' || article.topicSlug === activeTopic;
      if (!matchTopic) return false;
      if (!keyword) return true;
      const haystack = [article.title, article.excerpt, article.topicName, ...article.tags]
        .join(' ')
        .toLowerCase();
      return haystack.includes(keyword);
    });
  }, [q, activeTopic]);

  const currentTopic = topics.find((topic) => topic.slug === activeTopic);

  useSeo({
    title: currentTopic ? `${currentTopic.name} - 爆料库` : '爆料库 - 全部独家爆料',
    description: currentTopic
      ? `${currentTopic.desc} 看瓜爆料${currentTopic.name}栏目聚合全部相关独家爆料与深度分析。`
      : '看瓜爆料爆料库聚合全部独家一手私料，支持按主题分类筛选与关键词搜索，快速找到你关心的娱乐内幕。',
    keywords: `看瓜爆料,爆料库,${currentTopic ? currentTopic.name : '娱乐内幕'},独家爆料,一手私料`,
    path: '/topics',
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: currentTopic ? `看瓜爆料 · ${currentTopic.name}` : '看瓜爆料爆料库',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '爆料库', item: '/topics' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: filtered.slice(0, 10).map((article, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: article.title,
          url: `/article/${article.id}`,
        })),
      },
    ],
  });

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams(params);
    if (localQ.trim()) next.set('q', localQ.trim());
    else next.delete('q');
    setParams(next);
  };

  const selectTopic = (slug: string) => {
    const next = new URLSearchParams(params);
    if (slug === 'all') next.delete('topic');
    else next.set('topic', slug);
    setParams(next);
  };

  return (
    <SiteLayout>
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '爆料库', to: currentTopic ? '/topics' : undefined },
          ...(currentTopic ? [{ label: currentTopic.name }] : []),
        ]}
      />

      <section className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
        <header className="max-w-3xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
            <i className="ri-layout-grid-line text-sm"></i>
            全部爆料 · 按主题筛选
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
            {currentTopic ? `${currentTopic.name}` : '爆料库'}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            {currentTopic
              ? currentTopic.desc
              : '收录全部独家一手私料，支持关键词搜索与主题筛选。每篇都标注了信源核实情况与阅读时长，方便你按需查阅。'}
          </p>
        </header>

        <div className="mt-7 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => selectTopic('all')}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                activeTopic === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'border border-background-200/70 bg-background-100/60 text-foreground-700 hover:border-primary-300'
              }`}
            >
              全部
            </button>
            {topics.map((topic) => (
              <button
                key={topic.slug}
                type="button"
                onClick={() => selectTopic(topic.slug)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  activeTopic === topic.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-200/70 bg-background-100/60 text-foreground-700 hover:border-primary-300'
                }`}
              >
                {topic.name}
              </button>
            ))}
          </div>

          <form onSubmit={handleSearch} className="flex items-center rounded-full border border-background-200/70 bg-background-50 p-1.5 pl-4 lg:w-80">
            <i className="ri-search-line mr-2 text-base text-foreground-500"></i>
            <input
              type="search"
              value={localQ}
              onChange={(event) => setLocalQ(event.target.value)}
              placeholder="搜索标题、标签或栏目"
              aria-label="搜索爆料"
              className="min-w-0 flex-1 bg-transparent py-1.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-foreground-950 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-foreground-800"
            >
              搜索
            </button>
          </form>
        </div>

        <p className="mt-5 text-xs text-foreground-500">
          共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 条爆料
          {q && <span> · 关键词「{q}」</span>}
          {currentTopic && <span> · 栏目「{currentTopic.name}」</span>}
        </p>

        {filtered.length > 0 ? (
          <>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.slice(0, visible).map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
            {visible < filtered.length && (
              <div className="mt-9 text-center">
                <button
                  type="button"
                  onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                  className="cursor-pointer whitespace-nowrap rounded-full border border-background-300/70 bg-background-100/60 px-7 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  加载更多爆料
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300/70 bg-background-100/40 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-2xl text-secondary-700">
              <i className="ri-search-eye-line"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到相关爆料</h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              换个关键词试试，或点击下方按钮查看全部栏目内容。
            </p>
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(params);
                next.delete('q');
                next.delete('topic');
                setParams(next);
              }}
              className="mt-5 cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </section>
    </SiteLayout>
  );
};

export default Topics;